import {
    BucketAuthMethod,
    DeltaLakeDestinationOptions,
    getDefaultStorageAuthMethod,
    PreparedDestination,
} from "@prequel/react";
import { TimezoneConfig, TimezoneKey } from "./Constants";

type DeltaLakeConfigWithMisplacedAuth = DeltaLakeDestinationOptions & {
    bucket_auth_method?: BucketAuthMethod;
};

// Move bucket_auth_method into the nested storage config (e.g. delta_lake.bucket_abs) for Prequel API.
export const ensureDeltaLakeBucketAuthMethod = (
    preparedDestination: PreparedDestination,
    flatDestination: Record<string, unknown> = {}
): PreparedDestination => {
    if (preparedDestination.vendor !== 'delta_lake' || !preparedDestination.delta_lake) {
        return preparedDestination;
    }

    const vendorConfig = preparedDestination.delta_lake as DeltaLakeConfigWithMisplacedAuth;
    const storage = vendorConfig.storage;
    const bucketConfig = vendorConfig[storage];

    if (!bucketConfig || typeof bucketConfig !== 'object') {
        return preparedDestination;
    }

    if ('bucket_auth_method' in bucketConfig && bucketConfig.bucket_auth_method) {
        return preparedDestination;
    }

    const bucketAuthMethod =
        vendorConfig.bucket_auth_method ||
        (flatDestination.bucket_auth_method as BucketAuthMethod | undefined) ||
        getDefaultStorageAuthMethod(storage);

    if (!bucketAuthMethod) {
        return preparedDestination;
    }

    const { bucket_auth_method: _removed, ...vendorConfigWithoutMisplacedAuth } = vendorConfig;

    return {
        ...preparedDestination,
        delta_lake: {
            ...vendorConfigWithoutMisplacedAuth,
            [storage]: {
                ...bucketConfig,
                bucket_auth_method: bucketAuthMethod,
            },
        } as DeltaLakeDestinationOptions,
    };
};

export const TIMEZONE_CONFIG: Record<TimezoneKey, TimezoneConfig> = {
    local: {
        label: 'Local Timezone',
        getIdentifier: () => Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    utc: {
        label: 'UTC Timezone',
        getIdentifier: () => 'UTC'
    },
    pdt: {
        label: 'Pacific Timezone (PST/PDT)',
        getIdentifier: () => 'America/Los_Angeles'
    }
};

export const formatDateTime = (dateString: string, timezone: TimezoneKey, options?: Intl.DateTimeFormatOptions) => {
    if (!dateString) return '';
    const config = TIMEZONE_CONFIG[timezone];
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: config.getIdentifier(),
        ...options
    }).format(new Date(dateString));
};

export const formatDateTimeZone = (dateString: string, timezone: TimezoneKey): string => {
    if (!dateString) return '';
    const config = TIMEZONE_CONFIG[timezone];
    return new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: config.getIdentifier()
    }).format(new Date(dateString));
};

export const findNested = (obj: any, key: string): any => {
    // Base case: if obj is not an object or is null, return undefined
    if (typeof obj !== 'object' || obj === null) {
        return undefined;
    }
    // If the key is found at the current level, return its value
    if (obj.hasOwnProperty(key)) {
        return obj[key];
    }
    for (let prop in obj) {
        if (obj.hasOwnProperty(prop)) {
            const result = findNested(obj[prop], key);
            if (result !== undefined) {
                return result;
            }
        }
    }
    return undefined;
};